import type { KnowledgeView, ViewNavigation } from "@cloudox/api-client";

/**
 * The Reference Layer is published into the same navigation manifest as the
 * audience lenses, but it is not one of them: it is the deterministic
 * graph-derived reference tables (`cloudox/reference/`), scored by nobody and
 * addressed to whoever needs the underlying facts.
 *
 * The preview separates it so a reader does not read "Unknown confidence" — an
 * honest statement that confidence is carried per row, not per document — as a
 * weaker version of the narrated views.
 */
export const REFERENCE_VIEW_KEY = "reference";

export interface PartitionedViews {
  /** The audience lenses, in publication order. */
  readonly lenses: readonly KnowledgeView[];
  /** The Reference Layer, when this run published one. */
  readonly reference: KnowledgeView | undefined;
}

export function partitionViews(views: readonly KnowledgeView[]): PartitionedViews {
  const reference = views.find((v) => v.key === REFERENCE_VIEW_KEY);
  return {
    lenses: views.filter((v) => v.key !== REFERENCE_VIEW_KEY),
    ...(reference ? { reference } : { reference: undefined }),
  };
}

/**
 * When the *report* was generated — deliberately not called a discovery time.
 *
 * A run has three distinct timestamps (ADR-0007): when the environment was
 * discovered, when these artifacts were written, and when a control plane
 * recorded the run. Only the second is published in the static bundle, so it is
 * the only one this preview is entitled to show, and it has to be labelled as
 * what it is.
 */
export function formatGeneratedAt(iso: string | undefined): string | undefined {
  if (!iso) return undefined;
  const at = new Date(iso);
  if (Number.isNaN(at.getTime())) return undefined;
  return at.toLocaleDateString(undefined, {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
}

/** Route for a related intelligence page (Evolution / Cost / Quality). */
export function relatedPagePath(key: string): string {
  return `/pages/${encodeURIComponent(key)}`;
}

/** The query parameter that opens the CDX Agent panel. */
export const ASK_PARAM = "ask";

/**
 * Search string that opens the Agent panel, optionally on one answer.
 *
 * A parameter rather than a path because the Agent is a panel over the page a
 * reader is already on: routing to `/assistant` would replace the very
 * document they are asking about. Pathless, so React Router keeps the current
 * pathname and the panel opens in place — while the URL still carries the
 * open question, so a link to an answer survives being shared or refreshed.
 */
export function askSearch(key?: string): string {
  return key ? `?${ASK_PARAM}=${encodeURIComponent(key)}` : `?${ASK_PARAM}=`;
}

/** Route for a view, optionally deep-linked to one of its sections. */
export function viewPath(view: string, section?: string): string {
  return section
    ? `/views/${encodeURIComponent(view)}/${encodeURIComponent(section)}`
    : `/views/${encodeURIComponent(view)}`;
}

/** The related pages this run published, in manifest order. */
export function relatedPages(nav: ViewNavigation) {
  return nav.relatedPages;
}
